import {
    ArchiveBoxArrowDownIcon,
    ArchiveBoxXMarkIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { Avatar } from '../../Avatar';
import { formatCreatedDate } from '../../../utils/dates';
import type { PatientNote } from '../../../services/patientNotes';
import { SEVERITY } from './severity';
import { SeverityPill } from './SeverityPill';
import { VisibilityPill } from './VisibilityPill';
import { PinIcon } from './PinIcon';
import { NoteBody } from './NoteBody';

interface NoteRowBaseProps {
    note: PatientNote;
    /** Tighter padding + smaller type for narrow columns (e.g. the work queue). */
    compact?: boolean;
}

interface NoteRowActiveProps extends NoteRowBaseProps {
    view?: 'active';
    /** Whether to show the pin / edit / archive / delete controls (any staff member). */
    canManage: boolean;
    onEdit: (note: PatientNote) => void;
    onDelete: (noteId: string) => void;
    onPin: (noteId: string, pinned: boolean) => void;
    onArchive: (noteId: string) => void;
}

interface NoteRowArchivedProps extends NoteRowBaseProps {
    view: 'archived';
    onUnarchive: (noteId: string) => void;
    onDelete: (noteId: string) => void;
}

type NoteRowProps = NoteRowActiveProps | NoteRowArchivedProps;

function NoteRow(props: NoteRowProps) {
    const { note, compact = false } = props;
    const isArchived = props.view === 'archived';
    const s = SEVERITY[note.severity];
    const Icon = s.icon;

    // Row controls shrink with the row in compact mode.
    const actionBtn = compact ? 'p-1' : 'p-1.5';
    const actionIcon = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';

    const dateLabel =
        note.updatedAt !== note.createdAt
            ? `Updated ${formatCreatedDate(note.updatedAt)}`
            : formatCreatedDate(note.createdAt);

    return (
        <div
            className={`rounded-xl border ${s.border} ${s.bg} ${compact ? 'px-3 py-2.5' : 'px-4 py-3.5'} relative transition-all ${isArchived ? 'opacity-60 grayscale-[30%]' : ''}`}
        >
            <div className="flex items-start gap-3">
                <div className={`mt-0.5 flex-shrink-0 ${s.iconColor}`}>
                    <Icon className="w-4 h-4" />
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                            className={`${compact ? 'text-[12px]' : 'text-[13px]'} font-bold text-gray-900 font-inter`}
                        >
                            {note.title}
                        </span>
                        <SeverityPill severity={note.severity} size="xs" />
                        <VisibilityPill visibility={note.visibility} size="xs" />
                    </div>
                    <NoteBody note={note} compact={compact} />

                    <div className="flex items-center gap-2 mt-2.5">
                        <div className="w-[18px] h-[18px] flex-shrink-0">
                            <Avatar user={{ name: note.authorName, email: '' }} />
                        </div>
                        <span className="text-[11px] text-gray-500 font-inter">
                            {note.authorName}
                        </span>
                        <span className="text-gray-300 text-[10px]">·</span>
                        <span className="text-[11px] text-gray-400 font-inter">{dateLabel}</span>
                    </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                    {isArchived ? (
                        <>
                            <button
                                onClick={() => props.onUnarchive(note.id)}
                                className={`${actionBtn} rounded-lg bg-white/70 hover:bg-white border border-gray-200 text-gray-500 hover:text-gray-800 transition-colors`}
                                title="Unarchive note"
                            >
                                <ArchiveBoxXMarkIcon className={actionIcon} />
                            </button>
                            <button
                                onClick={() => props.onDelete(note.id)}
                                className={`${actionBtn} rounded-lg bg-white/70 hover:bg-white border border-gray-200 text-gray-400 hover:text-red-600 transition-colors`}
                                title="Delete note"
                            >
                                <TrashIcon className={actionIcon} />
                            </button>
                        </>
                    ) : props.canManage ? (
                        <>
                            <button
                                onClick={() => props.onPin(note.id, !note.pinned)}
                                className={`${actionBtn} rounded-lg bg-white/70 hover:bg-white border border-gray-200 transition-colors ${
                                    note.pinned ? s.iconColor : 'text-gray-500 hover:text-gray-800'
                                }`}
                                title={note.pinned ? 'Unpin note' : 'Pin note'}
                            >
                                <PinIcon className={actionIcon} />
                            </button>
                            <button
                                onClick={() => props.onEdit(note)}
                                className={`${actionBtn} rounded-lg bg-white/70 hover:bg-white border border-gray-200 text-gray-500 hover:text-gray-800 transition-colors`}
                                title="Edit note"
                            >
                                <PencilIcon className={actionIcon} />
                            </button>
                            <button
                                onClick={() => props.onArchive(note.id)}
                                className={`${actionBtn} rounded-lg bg-white/70 hover:bg-white border border-gray-200 text-gray-500 hover:text-gray-800 transition-colors`}
                                title="Archive note"
                            >
                                <ArchiveBoxArrowDownIcon className={actionIcon} />
                            </button>
                            <button
                                onClick={() => props.onDelete(note.id)}
                                className={`${actionBtn} rounded-lg bg-white/70 hover:bg-white border border-gray-200 text-gray-400 hover:text-red-600 transition-colors`}
                                title="Delete note"
                            >
                                <TrashIcon className={actionIcon} />
                            </button>
                        </>
                    ) : (
                        note.pinned && (
                            <PinIcon className={`${actionIcon} ${s.iconColor} opacity-60`} />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export { NoteRow };
